import {
  memo, useMemo, useRef,
} from '../../lib/teact/teact';

import type { ApiFormattedText, ApiMessage, ApiStory } from '../../api/types';
import type { ObserveFn } from '../../hooks/useIntersectionObserver';
import type { ThreadId } from '../../types';
import { ApiMessageEntityTypes } from '../../api/types';

import { extractMessageText, stripCustomEmoji } from '../../global/helpers';
import trimText from '../../util/trimText';
import { insertTextEntity, renderTextWithEntities } from './helpers/renderTextWithEntities';

import useLang from '../../hooks/useLang';
import useLastCallback from '../../hooks/useLastCallback';
import useSyncEffect from '../../hooks/useSyncEffect';
import useUniqueId from '../../hooks/useUniqueId';

import StreamingText from './StreamingText';
import TypingWrapper from './TypingWrapper';

/**
 * Fix broken markdown before feeding to StreamingText:
 * - Merge orphan `**` on its own line with the previous/next line
 * - Remove code fences wrapping tables (` ```\n| ... |` → just the table)
 */
function normalizeMarkdown(text: string): string {
  // 1. Fix bold markers split across lines:
  //    "text**\n" or "\n**" as orphan lines → merge into adjacent line
  let result = text
    // Closing ** on its own line → merge with previous line
    .replace(/\n\*\*\s*\n/g, '**\n\n')
    // Opening ** on its own line followed by text → merge with next line
    .replace(/\n\*\*\n([^\n*])/g, '\n**$1')
    // Trailing \n** at end → merge with previous content
    .replace(/([^\n])\n\*\*$/g, '$1**');

  // 2. Unwrap tables from code fences: ```\n| ... |\n``` → just the table lines
  result = result.replace(/```\n?((?:\|[^\n]*\n?)+)```/g, '$1');

  // 3. Merge broken table header rows:
  //    | col1 | col2 |\n col3 | col4 |\n|---|---| → | col1 | col2 | col3 | col4 |\n|---|---|
  result = result.replace(
    /(\|[^\n]+\|)\n([^|\n][^\n]*\|)/g,
    '$1 $2',
  );

  return result;
}

interface OwnProps {
  messageOrStory: ApiMessage | ApiStory;
  threadId?: ThreadId;
  forcedText?: ApiFormattedText;
  isForAnimation?: boolean;
  emojiSize?: number;
  highlight?: string;
  asPreview?: boolean;
  truncateLength?: number;
  isProtected?: boolean;
  observeIntersectionForLoading?: ObserveFn;
  observeIntersectionForPlaying?: ObserveFn;
  withTranslucentThumbs?: boolean;
  shouldRenderAsHtml?: boolean;
  inChatList?: boolean;
  forcePlayback?: boolean;
  focusedQuote?: string;
  focusedQuoteOffset?: number;
  isInSelectMode?: boolean;
  canBeEmpty?: boolean;
  maxTimestamp?: number;
  shouldAnimateTyping?: boolean;
  isStreamingContent?: boolean;
}

const MIN_CUSTOM_EMOJIS_FOR_SHARED_CANVAS = 3;

function MessageText({
  messageOrStory,
  forcedText,
  isForAnimation,
  emojiSize,
  highlight,
  asPreview,
  truncateLength,
  isProtected,
  observeIntersectionForLoading,
  observeIntersectionForPlaying,
  withTranslucentThumbs,
  shouldRenderAsHtml,
  inChatList,
  forcePlayback,
  focusedQuote,
  focusedQuoteOffset,
  isInSelectMode,
  canBeEmpty,
  maxTimestamp,
  threadId,
  shouldAnimateTyping,
  isStreamingContent,
}: OwnProps) {
  const sharedCanvasRef = useRef<HTMLCanvasElement>();
  const sharedCanvasHqRef = useRef<HTMLCanvasElement>();

  const textCacheBusterRef = useRef(0);

  const lang = useLang();

  // When streaming (or just finished), use incremental markdown renderer to avoid layout jump
  const rawTextForStreaming = !forcedText ? extractMessageText(messageOrStory, inChatList)?.text : undefined;
  if (isStreamingContent !== undefined && rawTextForStreaming) {
    return (
      <StreamingText text={rawTextForStreaming} isStreaming={isStreamingContent} />
    );
  }

  // If translation returned the same text as original (e.g. same language), discard it
  // so the original entities (bold, etc.) are preserved.
  const originalFormattedText = extractMessageText(messageOrStory, inChatList);
  const isTranslationSameAsOriginal = forcedText && forcedText.text
    && forcedText.text === originalFormattedText?.text;
  const effectiveForcedText = isTranslationSameAsOriginal ? undefined : forcedText;

  // Translated text with no entities — render as markdown via StreamingText.
  if (effectiveForcedText && effectiveForcedText.text
    && (!effectiveForcedText.entities || effectiveForcedText.entities.length === 0)) {
    return (
      <StreamingText text={normalizeMarkdown(effectiveForcedText.text)} isStreaming={false} />
    );
  }

  const formattedText = effectiveForcedText || originalFormattedText;
  const adaptedFormattedText = isForAnimation && formattedText ? stripCustomEmoji(formattedText) : formattedText;
  const { text, entities } = adaptedFormattedText || {};

  const entitiesWithFocusedQuote = useMemo(() => {
    if (!text || !focusedQuote) return entities;

    const offsetIndex = text.indexOf(focusedQuote, focusedQuoteOffset);
    const index = offsetIndex >= 0 ? offsetIndex : text.indexOf(focusedQuote); // Fallback to first occurrence
    const lendth = focusedQuote.length;
    if (index >= 0) {
      return insertTextEntity(entities || [], {
        offset: index,
        length: lendth,
        type: ApiMessageEntityTypes.QuoteFocus,
      });
    }

    return entities;
  }, [text, entities, focusedQuote, focusedQuoteOffset]);

  const containerId = useUniqueId();

  useSyncEffect(() => {
    textCacheBusterRef.current += 1;
  }, [text, entitiesWithFocusedQuote]);

  const withSharedCanvas = useMemo(() => {
    const hasSpoilers = entitiesWithFocusedQuote?.some((e) => e.type === ApiMessageEntityTypes.Spoiler);
    if (hasSpoilers) {
      return false;
    }

    const customEmojisCount = entitiesWithFocusedQuote
      ?.filter((e) => e.type === ApiMessageEntityTypes.CustomEmoji).length || 0;
    return customEmojisCount >= MIN_CUSTOM_EMOJIS_FOR_SHARED_CANVAS;
  }, [entitiesWithFocusedQuote]) || 0;

  const renderText = useLastCallback((t: ApiFormattedText) => {
    return renderTextWithEntities({
      text: t.text,
      entities: t.entities,
      highlight,
      emojiSize,
      shouldRenderAsHtml,
      containerId,
      asPreview,
      isProtected,
      observeIntersectionForLoading,
      observeIntersectionForPlaying,
      withTranslucentThumbs,
      sharedCanvasRef,
      sharedCanvasHqRef,
      cacheBuster: textCacheBusterRef.current.toString(),
      forcePlayback,
      isInSelectMode,
      maxTimestamp,
      chatId: 'chatId' in messageOrStory ? messageOrStory.chatId : undefined,
      messageId: messageOrStory.id,
      threadId,
    });
  });

  if (!text && !canBeEmpty) {
    return <span className="content-unsupported">{lang('MessageUnsupported')}</span>;
  }

  const textToRender: ApiFormattedText = {
    text: trimText(text || '', truncateLength),
    entities: entitiesWithFocusedQuote,
  };

  return (
    <>
      {[
        withSharedCanvas && <canvas key="shared-canvas" ref={sharedCanvasRef} className="shared-canvas" />,
        withSharedCanvas && <canvas key="shared-canvas-hq" ref={sharedCanvasHqRef} className="shared-canvas" />,
        shouldAnimateTyping ? (
          <TypingWrapper key="typing-wrapper" text={textToRender}>{renderText}</TypingWrapper>
        ) : renderText(textToRender),
      ].flat().filter(Boolean)}
    </>
  );
}

export default memo(MessageText);
